import * as Yup from "yup";
export const atLeastOneField = "At least one option must be selected";
export const generalErrorMessage = "Field is required";
const createConditionalStringSchema = (dependsOnField, expectedValue, errorMsg, minValue) => 
  Yup.string().when(dependsOnField, {
    is: (value) => value && value === expectedValue,
    then: () => Yup.string().required(errorMsg).min(minValue || 1, errorMsg),
    otherwise: () => Yup.string().optional(),
});

const createConditionalArraySchema = (dependsOnField, expectedValue, errorMsg, minValue) => 
  Yup.array(Yup.string()).when(dependsOnField, {
    is: (value) =>value && value === expectedValue,
    then: () => Yup.array(Yup.string()).required(errorMsg).min(minValue || 1, errorMsg),
    otherwise: () => Yup.array().optional(),
  });                                              

export const validationSchema = Yup.object({
  card_enrollment_manage_enrollment_type: Yup.string().optional(),

  // Section 1: Platform linking fields
  card_enrollment_link_enrollment_platform_to_i2c: createConditionalStringSchema(
    "card_enrollment_manage_enrollment_type",
    "Manage enrollment outside i2c platform",
    generalErrorMessage
  ),

  card_enrollment_card_enrollment_url_type: createConditionalStringSchema(
    "card_enrollment_link_enrollment_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_card_enrollment_url: createConditionalStringSchema(
    "card_enrollment_card_enrollment_url_type",
    "Custom URL",
    generalErrorMessage
  ),

  card_enrollment_url_platform: createConditionalStringSchema(
    "card_enrollment_link_enrollment_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  // Section 2: i2c platform fields
  card_enrollment_url_platform: createConditionalArraySchema(
    "card_enrollment_manage_enrollment_type",
    "Manage enrollment through i2c platform",
    generalErrorMessage
  ),

  card_enrollment_card_enrollment_url_type: createConditionalStringSchema(
    "card_enrollment_manage_enrollment_type",
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_card_account_type: Yup.string().when("card_enrollment_manage_enrollment_type", {
    is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type == "Manage enrollment through i2c platform",
    then: () => Yup.string().required(generalErrorMessage),
    otherwise: () => Yup.string().optional(),
}),



  card_enrollment_category_consumer_link_platform_to_i2c: createConditionalStringSchema(
    "card_enrollment_manage_enrollment_type",
    "Manage enrollment through i2c platform",
    generalErrorMessage
  ),

  card_enrollment_category_consumer_title_for_enrollment_screen: createConditionalStringSchema(
    "card_enrollment_category_consumer_link_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_category_consumer_description_for_enrollment_screen: createConditionalStringSchema(
    "card_enrollment_category_consumer_link_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  // Section 3: Business fields
  card_enrollment_category_business_link_platform_to_i2c: createConditionalStringSchema(
    "card_enrollment_manage_enrollment_type",
    "Manage enrollment through i2c platform",
    generalErrorMessage
  ),

  card_enrollment_category_business_title_for_enrollment_screen: createConditionalStringSchema(
    "card_enrollment_category_business_link_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_category_business_description_for_enrollment_screen: createConditionalStringSchema(
    "card_enrollment_category_business_link_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_category_corporate_link_platform_to_i2c: createConditionalStringSchema(
    "card_enrollment_manage_enrollment_type",
    "Manage enrollment through i2c platform",
    generalErrorMessage
  ),

  card_enrollment_category_corporate_title_for_enrollment_screen: createConditionalStringSchema(
    "card_enrollment_category_corporate_link_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_category_corporate_description_for_enrollment_screen: createConditionalStringSchema(
    "card_enrollment_category_corporate_link_platform_to_i2c",
    "Yes",
    generalErrorMessage
  ),

  // Section 4: Product group and agreement fields
  card_enrollment_card_product_groups: Yup.array().when("brand_info_common_brand_contact_us_feature_type", {
    is: (type) => type === "Use External URL",
    then: () =>
      Yup.array().of(
        Yup.object({
          image_file: Yup.array(Yup.string()).nullable(),
          count: Yup.number().required(generalErrorMessage),
          title: Yup.string().required(generalErrorMessage),
          description: Yup.string().required(generalErrorMessage),
        })
      ).min(1, generalErrorMessage),
    otherwise: () => Yup.array().optional(),
  }),

  card_enrollment_agreement_for_customer_acceptance: createConditionalStringSchema(
    "card_enrollment_manage_enrollment_type",
    "Manage enrollment through i2c platform",
    generalErrorMessage
  ),

  card_enrollment_agreement_type: createConditionalStringSchema(
    ["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_for_customer_acceptance"],
    "Yes",
    generalErrorMessage
  ),

  card_enrollment_agreement_external_content_url: createConditionalStringSchema(
    ["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_type"],
    "Use External URL",
    generalErrorMessage
  ),

  card_enrollment_agreement_customized_content_file: Yup.array(Yup.string()).when(["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_type"], {
    is: (type, agreementType) => type === "Manage enrollment through i2c platform" && agreementType === "Use Customized Content",
    then: () => Yup.array(Yup.string()).optional(),
    otherwise: () => Yup.array().optional(),
  }),

  // Section 5: Info arrays
  card_enrollment_shipping_method: Yup.array().of(
    Yup.object().shape({
      feature: Yup.string().optional(),
      enabled: Yup.boolean(),
      alternativeMethod: Yup.string().optional(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_login_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_business_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_business_owner_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_employee_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_documentation: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_additional_card_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_corporate_account_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
 
  card_enrollment_fields_for_business_login_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_business_business_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_business_business_owner_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_business_employee_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_business_documentation: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_business_additional_card_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_business_account_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),

  card_enrollment_fields_for_consumer_source_of_income_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_consumer_personal_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_consumer_login_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_consumer_identification_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
      deleteable: Yup.boolean(),
    })
  ).nullable(),
  card_enrollment_fields_for_consumer_documentary_proof_info: Yup.array().of(
    Yup.object().shape({
      enrollment_field: Yup.string().optional(),
      enable: Yup.boolean(),
      mandatory: Yup.boolean(),
      disabled: Yup.boolean(),
      deleteable: Yup.boolean(),
    })
  ).nullable(),
   // Conditionally required fields when type === "prepaid"
   card_enrollment_funding_option: Yup.array(Yup.string()).when('type', {
    is: (type) => type === 'prepaid',
    then: () => Yup.array(Yup.string()).required(generalErrorMessage),
    otherwise: () => Yup.array().optional(),
  }),

  card_enrollment_funding_option_payment_gateway: Yup.string().when('type', {
    is: (type) => type === 'prepaid',
    then: () => Yup.string().required('Payment gateway is required for prepaid type.'),
    otherwise: () => Yup.string().optional(),
  }),

  card_enrollment_funding_option_payment_gateway_other: Yup.string().when('type', {
    is: (type) => type === 'prepaid',
    then: () => Yup.string().required('Other payment gateway is required for prepaid type.'),
    otherwise: () => Yup.string().optional(),
  }),

});
export const fields = [
  "card_enrollment_manage_enrollment_type",
  "card_enrollment_link_enrollment_platform_to_i2c",
  "card_enrollment_card_enrollment_url_type",
  "card_enrollment_card_enrollment_url",
  "card_enrollment_category_consumer_link_platform_to_i2c",
  "card_enrollment_category_consumer_title_for_enrollment_screen",
  "card_enrollment_category_consumer_description_for_enrollment_screen",
  "card_enrollment_category_business_link_platform_to_i2c",
  "card_enrollment_category_business_title_for_enrollment_screen",
  "card_enrollment_category_business_description_for_enrollment_screen",
  "card_enrollment_category_corporate_link_platform_to_i2c",
  "card_enrollment_category_corporate_title_for_enrollment_screen",
  "card_enrollment_category_corporate_description_for_enrollment_screen",
  // "card_enrollment_card_products_count",
  "card_enrollment_consumer_credit_card",
  "card_enrollment_create_user_during_enrollment",
  "card_enrollment_customer_email_as_user_id",
  "card_enrollment_show_email_id_on_screen",
  "card_enrollment_agreement_for_customer_acceptance",
  "card_enrollment_agreement_type",
  "card_enrollment_agreement_external_content_url",
  "card_enrollment_agreement_type_for_common_toggle",
  "card_enrollment_card_account_type",
 
  "card_enrollment_funding_option_payment_gateway",
  "card_enrollment_funding_option_payment_gateway_other"
];
export const fieldsArray = [
  "card_enrollment_funding_option",
  "card_enrollment_agreement_customized_content_file",
  "card_enrollment_card_product_groups",
  "card_enrollment_shipping_method", 
  "card_enrollment_url_platform",
  "card_enrollment_fields_for_corporate_login_info",
  "card_enrollment_fields_for_corporate_account_info",
  "card_enrollment_fields_for_corporate_additional_card_info",
  "card_enrollment_fields_for_corporate_documentation",
  "card_enrollment_fields_for_corporate_employee_info",
  "card_enrollment_fields_for_corporate_business_owner_info",
  "card_enrollment_fields_for_corporate_business_info",

  "card_enrollment_fields_for_business_login_info",
  "card_enrollment_fields_for_business_account_info",
  "card_enrollment_fields_for_business_additional_card_info",
  "card_enrollment_fields_for_business_documentation",
  "card_enrollment_fields_for_business_employee_info",
  "card_enrollment_fields_for_business_business_owner_info",
  "card_enrollment_fields_for_business_business_info",

  "card_enrollment_fields_for_consumer_source_of_income_info",
  "card_enrollment_fields_for_consumer_personal_info",
  "card_enrollment_fields_for_consumer_login_info",
  "card_enrollment_fields_for_consumer_identification_info",
  "card_enrollment_fields_for_consumer_documentary_proof_info",
];
// export const validationSchema = Yup.object({
//     card_enrollment_manage_enrollment_type: Yup.string().optional(),
//     card_enrollment_link_enrollment_platform_to_i2c: Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment outside i2c platform",
//         then: () =>
//             Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_card_enrollment_url_type: Yup.string().when("card_enrollment_link_enrollment_platform_to_i2c", {
//         is: (card_enrollment_link_enrollment_platform_to_i2c) => card_enrollment_link_enrollment_platform_to_i2c === "Yes",
//         then: () =>
//             Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }), 
//     card_enrollment_card_enrollment_url: Yup.string().when("card_enrollment_card_enrollment_url_type", {
//         is: (card_enrollment_card_enrollment_url_type) => card_enrollment_card_enrollment_url_type === "Custom URL",
//         then: () =>
//             Yup.string()
//                 .required(generalErrorMessage),
//         otherwise: () => Yup.string().optional(),
//     }), 
//     card_enrollment_url_platform: Yup.string().when("card_enrollment_link_enrollment_platform_to_i2c", {
//         is: (card_enrollment_link_enrollment_platform_to_i2c) => card_enrollment_link_enrollment_platform_to_i2c === "Yes",
//         then: () =>
//             Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }), 
//     // First option completed
//     card_enrollment_url_platform: Yup.array(Yup.string()).when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//         then: () =>
//             Yup.array(Yup.string())
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.array().optional(),
//     }),
//     card_enrollment_card_enrollment_url_type: Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Yes",
//         then: () =>
//             Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }), 
//     card_enrollment_card_account_type: Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage) ,
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_consumer_link_platform_to_i2c:  Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_consumer_title_for_enrollment_screen: Yup.string().when("card_enrollment_category_consumer_link_platform_to_i2c", {
//         is: (card_enrollment_category_consumer_link_platform_to_i2c) => card_enrollment_category_consumer_link_platform_to_i2c === "Yes",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_consumer_description_for_enrollment_screen: Yup.string().when("card_enrollment_category_consumer_link_platform_to_i2c", {
//         is: (card_enrollment_category_consumer_link_platform_to_i2c) => card_enrollment_category_consumer_link_platform_to_i2c === "Yes",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_business_link_platform_to_i2c:  Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_business_title_for_enrollment_screen: Yup.string().when("card_enrollment_category_business_link_platform_to_i2c", {
//         is: (card_enrollment_category_business_link_platform_to_i2c) => card_enrollment_category_business_link_platform_to_i2c === "Yes",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_business_description_for_enrollment_screen: Yup.string().when("card_enrollment_category_business_link_platform_to_i2c", {
//         is: (card_enrollment_category_business_link_platform_to_i2c) => card_enrollment_category_business_link_platform_to_i2c === "Yes",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_corporate_link_platform_to_i2c:  Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_corporate_title_for_enrollment_screen: Yup.string().when("card_enrollment_category_corporate_link_platform_to_i2c", {
//         is: (card_enrollment_category_corporate_link_platform_to_i2c) => card_enrollment_category_corporate_link_platform_to_i2c === "Yes",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_category_corporate_description_for_enrollment_screen: Yup.string().when("card_enrollment_category_corporate_link_platform_to_i2c", {
//         is: (card_enrollment_category_corporate_link_platform_to_i2c) => card_enrollment_category_corporate_link_platform_to_i2c === "Yes",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     // card_enrollment_card_products_images_file: Yup.array(Yup.string()).when("card_enrollment_manage_enrollment_type", {
//     //     is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//     //     then: () =>
//     //     Yup.array(Yup.string()).nullable(),
//     //     otherwise: () =>  Yup.array(Yup.string()).nullable(),
//     // }),
//     // card_enrollment_card_products_count: Yup.number().when("card_enrollment_manage_enrollment_type", {
//     //     is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//     //     then: () =>
//     //     Yup.number().required(generalErrorMessage),
//     //     otherwise: () => Yup.number().optional(),
//     // }),
//     card_enrollment_card_product_groups: Yup.array().when("brand_info_common_brand_contact_us_feature_type", {
//         is: (brand_info_common_brand_contact_us_feature_type) => brand_info_common_brand_contact_us_feature_type === "Use External URL",
//         then: () =>
//             Yup.array()
//                 .of(
//                     Yup.object({
//                         image_file: Yup.array(Yup.string()).nullable(),
//                         count:  Yup.number().required(generalErrorMessage),
//                         title: Yup.string()
//                             .required(generalErrorMessage)
//                             .min(1, generalErrorMessage),
//                         description: Yup.string()
//                             .required(generalErrorMessage)
//                             .min(1, generalErrorMessage),
//                     }),
//                 )
//                 .min(1, generalErrorMessage),
//         otherwise: () => Yup.array().optional(),
//     }),
//     card_enrollment_consumer_credit_card: Yup.string().when("card_enrollment_manage_enrollment_type", {
//         is: (card_enrollment_manage_enrollment_type) => card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform",
//         then: () =>
//         Yup.string()
//                 .required(generalErrorMessage)
//                 .min(1, atLeastOneField),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_create_user_during_enrollment: Yup.string().when("card_enrollment_manage_enrollment_type",{
//         is: (value) => value === "Manage enrollment through i2c platform",
//         then: () => Yup.string().required(generalErrorMessage),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_customer_email_as_user_id : Yup.string().when("card_enrollment_manage_enrollment_type",{
//         is: (value) => value === "Manage enrollment through i2c platform",
//         then: () => Yup.string().required(generalErrorMessage),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_show_email_id_on_screen : Yup.string().when("card_enrollment_customer_email_as_user_id",{
//         is: (value) => value && value === "No",
//         then: () => Yup.string().required(generalErrorMessage),
//         otherwise: () => Yup.string().optional(),
//     }),
//     card_enrollment_agreement_for_customer_acceptance: Yup.string().when("card_enrollment_manage_enrollment_type",{
//         is: (value) => value && value === "Manage enrollment through i2c platform",
//         then: ()=> Yup.string().required(generalErrorMessage),
//         otherwise: ()=> Yup.string().optional(),
//     }),
//     card_enrollment_agreement_type_for_common_toggle: Yup.string().when(["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_for_customer_acceptance"],{
//         is: (card_enrollment_manage_enrollment_type, card_enrollment_agreement_for_customer_acceptance) =>
//         card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform" &&
//         card_enrollment_agreement_for_customer_acceptance=== "Yes",
//         then: () => Yup.string().required(generalErrorMessage).min(1,atLeastOneField),
//         otherwise: () => Yup.string().optional(), 
//     }), 
//     card_enrollment_agreement_type: Yup.string().when(["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_for_customer_acceptance"],{
//         is: (card_enrollment_manage_enrollment_type, card_enrollment_agreement_for_customer_acceptance) =>
//         card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform" &&
//         card_enrollment_agreement_for_customer_acceptance=== "Yes",
//             then: () => Yup.string().required(generalErrorMessage),
//             otherwise: () => Yup.string().optional(),
//         }
//     ),
//     card_enrollment_agreement_external_content_url: Yup.string().when(["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_type"],{
//         is: (card_enrollment_manage_enrollment_type, card_enrollment_agreement_type) =>
//         card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform" &&
//         card_enrollment_agreement_type === "Use External URL",
//         then: () => Yup.string().required(generalErrorMessage),
//         otherwise: () => Yup.string().optional(),
//     }), 
//     card_enrollment_agreement_customized_content_file: Yup.array(Yup.string()).when(["card_enrollment_manage_enrollment_type", "card_enrollment_agreement_type"],{
//         is: (card_enrollment_manage_enrollment_type, card_enrollment_agreement_type) =>
//         card_enrollment_manage_enrollment_type === "Manage enrollment through i2c platform" &&
//         card_enrollment_agreement_type === "Use Customized Content",
//         then: () => Yup.array(Yup.string()).optional(),
//         otherwise: () => Yup.array().optional(),
//     }),
//     card_enrollment_login_info: Yup.array().of(
//         Yup.object({
//           enrollment_field: Yup.string().required(generalErrorMessage),
//           enable: Yup.boolean().required(generalErrorMessage),
//           mandatory: Yup.boolean().required(generalErrorMessage),
//         })
//       ),
//       card_enrollment_business_info: Yup.array().of(
//         Yup.object({
//           business_info_enrollment_field: Yup.string().required(generalErrorMessage),
//           business_info_enable: Yup.boolean().required(generalErrorMessage),
//           business_info_mandatory: Yup.boolean().required(generalErrorMessage),
//         })
//       ),
//       card_enrollment_business_owner_info: Yup.array().of(
//         Yup.object({
//           business_owner_enrollment_field: Yup.string().required(generalErrorMessage),
//           business_owner_enable: Yup.boolean().required(generalErrorMessage),
//           business_owner_mandatory: Yup.boolean().required(generalErrorMessage),
//         })
//       ),
  
// });
